import { addHours, eachMinuteOfInterval, startOfDay, subMilliseconds } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { range } from 'lodash-es';
import { useMemo } from 'react';
import { useLocale } from '../../../utils/date-fns';
import { CALENDAR_SETTINGS, TIME_FORMATS } from '../constants';
import { calendarTimezone, toCalendarDate } from '../utils';
import type { Day } from './useCalendarScales';

interface CalendarTimeResult {
    startOfWeek: Date;
    days: Day[];
    timeColumns: string[];
}

export function useCalendarTime(
    inViewStartDate: Date,
    timezone: string,
    dayStartOffset: number
): CalendarTimeResult {
    const locale = useLocale();

    const startOfWeek = useMemo(() => {
        // Step 1: Convert the inViewStartDate (local browser date) to the.
        // specified timezone (e.g., 'Europe/Amsterdam').
        const inViewStartDateZoned = utcToZonedTime(inViewStartDate, timezone);

        // Step 2: Get the start of the day and add our 'start of the day offset' to it.
        const startOfWeekZoned = addHours(startOfDay(inViewStartDateZoned), dayStartOffset);
        // Step 3: Convert the zoned start of the week to UTC.
        return toCalendarDate(startOfWeekZoned);
    }, [inViewStartDate, timezone, dayStartOffset]);

    const days = useMemo(
        () =>
            range(CALENDAR_SETTINGS.DAYS_IN_WEEK).map((day) => {
                const start = addHours(startOfWeek, day * CALENDAR_SETTINGS.HOURS_IN_DAY);
                const end = subMilliseconds(addHours(start, CALENDAR_SETTINGS.HOURS_IN_DAY), 1);

                return {
                    start,
                    end,
                    // Creates the name of the day in the locale provided.
                    name: formatInTimeZone(start, calendarTimezone, TIME_FORMATS.DAY_NAME, {
                        locale,
                    }),
                    // Creates a unique key that looks like `2022-365` that can be used in React.
                    key: formatInTimeZone(start, calendarTimezone, TIME_FORMATS.DATE_KEY, {
                        // This option is not yet in the type definitions of date-fns,
                        // but it exists in the code.
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        useAdditionalDayOfYearTokens: true,
                    }),
                };
            }),
        [startOfWeek, locale]
    );
    // Create time columns by dividing the first day in 'days' into 60-minute intervals
    // and formatting each interval's hour in the calendar timezone.
    const timeColumns = useMemo(
        () =>
            eachMinuteOfInterval(
                {
                    start: days[0].start,
                    end: days[0].end,
                },
                { step: 60 }
            ).map((d) => formatInTimeZone(d, calendarTimezone, TIME_FORMATS.HOUR_MINUTE)),
        [days]
    );

    return { startOfWeek, days, timeColumns };
}
